<template>
  <!-- 导航栏 -->
  <header :style="{
    '--bg-opacity': !isHome ? 1 : Math.min(scrollY / maxScrollForEffect, 1),
  }" ref="headerRef">
    <div class="navBox contentBox">
      <div class="leftLogo" @click="navTo('/home')">
        <div>耀客物联</div>
        <OssImg class="logoImg" src="images/logo.png" alt="" />
      </div>

      <div class="menuBox display_flex" style="padding: 0 20px" @mouseleave="hoverMenu = -1">
        <div v-for="(items, index) in menu" :key="index" class="menu" @mouseenter="hoverMenu = index"
          @mouseleave="hoverMenu = -1">
          <div class="menuTitle" :style="{
            color: isMenuActive(items.path, activeMenu) ? '#32A89E' : ''
          }" @click.stop="gotoMenu(items)">
            {{ items.title }}
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts" setup>
import { isMobie, navTo } from "@/utils/util";
import { useRouter } from "vue-router";
import {
  getCurrentInstance,
  onMounted,
  ref,
  watch,
  onBeforeUnmount,
  defineExpose,
} from "vue";
import MenuOptions from "@/utils/menu";
import OssImg from "./ossImg.vue";

const props = defineProps({
  //是否首页
  isHome: {
    type: Boolean,
    default: false,
  },
});

const { proxy } = getCurrentInstance() as any;
const router = useRouter();

const activeMenu = ref("");
const hoverMenu = ref(-1); // 当前悬停的菜单索引

// 判断当前路径是否属于某个根菜单
const isMenuActive = (menuPath: string, currentPath: string): boolean => {
  // 处理菜单路径中的 /index 后缀
  const normalizeMenuPath = menuPath.replace(/\/index$/, '');
  
  // 如果路径完全匹配
  if (currentPath === menuPath || currentPath === normalizeMenuPath) {
    return true;
  }
  
  // 检查是否是根路径的子路径（处理 /news/index 和 /news/detail 等情况）
  if (currentPath.startsWith(normalizeMenuPath + '/')) {
    return true;
  }
  
  // 特殊处理首页路径
  if (normalizeMenuPath === '/home' && (currentPath === '/' || currentPath === '' || currentPath === '/home')) {
    return true;
  }
  
  // 特殊处理客户案例详情页
  if (menuPath === '/customerCase' && currentPath.startsWith('/customerCase')) {
    return true;
  }
  
  return false;
};
const scrollY = ref(0);
const maxScrollForEffect = 500;
const scrollLeft = ref(0);
const menu = ref(MenuOptions);
const headerRef = ref(null);

//移动端抽屉
const isDrawerOpen = ref(false);
const openIndex = ref(0);
const openSubIndex = ref(0);

// 监听路由变化
watch(
  () => router.currentRoute.value,
  (newValue: any) => {
    activeMenu.value = newValue.path;
  },
  { immediate: true, deep: true }
);

// 点击菜单跳转
const gotoMenu = (items) => {
  console.log(items);
  if (items.children) {
    return;
  }
  proxy.$router.push(items.path);
};

// 滚动监听
const handleScroll = () => {
  scrollLeft.value =
    document.documentElement.scrollLeft || document.body.scrollLeft;
  scrollY.value = window.scrollY;
};

function toggleDrawer() {
  console.log("toggleDrawer");
  isDrawerOpen.value = !isDrawerOpen.value; // 切换抽屉状态
}

/**
 * 点击一级菜单
 * @param item
 * @param index
 */
function toggleItem(item, index) {
  if (item.children) {
    // 展开或折叠二级菜单
    openIndex.value = openIndex.value === index ? null : index;
  } else {
    gotoMenu(item);
    isDrawerOpen.value = false;
  }
}
/**
 *
 * 点击二级菜单
 * @param subItem
 * @param index
 */
function toggleSubItem(subItem, index) {
  if (subItem.children) {
    openSubIndex.value = openSubIndex.value === index ? null : index;
  } else {
    gotoMenu(subItem);
    isDrawerOpen.value = false;
  }
}

onMounted(() => {
  window.addEventListener("scroll", handleScroll, true);
  handleScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll, true);
});

defineExpose({
  ref: headerRef.value,
});
</script>

<style lang="scss" scoped>
header {
  background-color: rgba(247, 248, 250, var(--bg-opacity, 0));
  transition: background-color 0.3s ease;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
}

.navBox {
  height: 5rem;
  display: flex;
  align-items: center;
  padding: 0 3.5rem;

  .leftLogo {
    color: #212121;
    font-family: "PingFang SC";
    font-size: 1.5rem; // 24px/16
    font-style: normal;
    font-weight: 600;
    text-align: justify;
    line-height: 2.125rem; // 34px/16
    width: 6.0625rem; // 97px/16
    height: 2.8125rem; // 45px/16
    cursor: pointer;

    .logoImg {
      width: 6.0625rem; // 97px/16
      height: 0.75rem; // 12px/16
      display: block;
    }
  }
}

.menuBox {
  margin-left: 5rem;
}

.menu {
  height: 5rem;
  position: relative;
  padding: 0 2rem; // 35px/16
  display: flex;
  align-items: center;

  .menuTitle {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    transition: color 0.3s;
    font-weight: bold;
    font-size: 1rem;
    color: #212121;

    &:hover {
      color: #32a89e;
    }
  }

  .children {
    position: absolute;
    top: 5rem; // 120px/16
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s, visibility 0.3s;
    z-index: 100;

    &.visible {
      opacity: 1;
      visibility: visible;
    }
  }

  .childrenBgc {
    background: white;
    border-radius: 1rem; // 16px/16
    box-shadow: 0 0 1.25rem rgba(0, 0, 0, 0.1); // 20px/16
    padding: 1rem; // 16px/16
    margin-top: 0.625rem; // 10px/16
  }

  .childrenItem {
    padding: 0.5rem 1rem; // 8px/16 16px/16
    margin-right: 1rem; // 16px/16
    min-width: 7.0625rem; // 113px/16
    box-sizing: border-box;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      color: #32a89e;
    }
  }
}

.thirdMenuItem {
  font-size: 0.9rem; // 16px/16
  line-height: 1.25rem; // 20px/16
  font-weight: 400;
  padding: 0.5625rem 0; // 9px/16
  color: #333;
  display: flex;
  align-items: center;

  &:hover {
    color: #32a89e;
  }

  &::before {
    content: "";
    display: block;
    width: 0.0625rem; // 1px/16
    height: 0.625rem; // 10px/16
    background: #888;
    margin: 0 1rem; // 16px/16
  }
}

.thirdMenuItem:first-of-type::before {
  display: none;
}

.drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: 75%;
  height: 100%;
  background-color: white;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;
  }

  .logo {
    font-size: 20px;
    font-weight: bold;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
  }

  .menu-list {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 100vh;
    overflow-y: auto;
  }

  .menu-item {
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid #eee;
  }

  .menu-title {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    font-weight: 500;
  }

  .sub-menu-item {
    width: 100%;
    padding: 8px 0;
  }

  .sub-menu-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    padding: 8px 16px;
    color: #555;
  }

  .children {
    width: 100%;
    list-style: none;
    padding: 0;
    margin: 0;
    background-color: #f9f9f9;
  }

  .third-level-menu {
    list-style: none;
    padding: 0 0 0 24px;
    margin: 0;
    background-color: #f0f0f0;

    li {
      padding: 4px 0;
    }
  }

  .third-menu-item {
    padding: 8px 16px;
    font-size: 13px;
    color: #666;

    &:active {
      color: #32a89e;
      background-color: #e0e0e0;
    }
  }

  .arrow {
    font-size: 12px;
    margin-left: 8px;
  }
}

.open {
  transform: translateX(0);
}

// @media (max-width:768px) {
//   .menuBox {
//     display: none;
//   }
// }</style>
